import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { RecordData, Service } from 'webapp-types';
import type { UserLevel } from 'webapp/enums/enums';
import { BillingFeature } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import UserAdminInviteComponent from 'webapp/components/user/admin-invite';
import UserNonAdminInviteComponent from 'webapp/components/user/non-admin-invite';

interface Args {
	onAdminInvite: () => void;
	onClose: () => void;
	onContentChange: () => void;
	maximumNumberOfNewUsers?: number;
	initialNumberOfNewUsers?: number;
	ignoreEmptyFields?: boolean;
	trackingOrigin?: string;
	onUserInvited?: (users: RecordData.User.Raw[]) => void;
	onNonAdminInvite?: () => void;
}

export interface InviteUserData {
	email: string;
	level: UserLevel;
}

export default class UserInviteComponent extends Component<Args> {
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('request') declare requestService: Service.Request;
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('store') declare storeService: Service.Store;

	@tracked showInviteForm = true;
	@tracked submitInProgress = false;

	get maximumNumberOfNewUsers(): number {
		return Math.min(this.availableNumberOfUsers, this.args.maximumNumberOfNewUsers || 10);
	}

	get availableNumberOfUsers(): number {
		const maxUsers =
			(this.billingService.getFeatureValue(BillingFeature.USERS) as number) || Infinity;
		return maxUsers - this.billingService.getFeatureCurrent(BillingFeature.USERS);
	}

	get trackingOrigin() {
		return this.args.trackingOrigin || 'user-management';
	}

	@action
	invite(users: InviteUserData[]): void {
		if (!users) {
			return;
		}

		this.submitInProgress = true;

		if (this.billingService.checkIfCan(BillingFeature.USERS, true)) {
			this.requestService
				.api<RecordData.User.Raw[]>('POST', '/users/batch', users)
				.then((userRecords) => {
					if (this.args.onUserInvited) {
						this.args.onUserInvited(userRecords);
					}

					this.storeService.pushPayload({ users: userRecords });

					userRecords.forEach((userRecord) => {
						this.analyticsService.event(TrackingEvent.NEW_USER_ADDED, {
							user_id: userRecord.id,
							origin: this.trackingOrigin,
						});
					});

					if (this.currentUserService.isAdmin) {
						this.infolayerService.ok(
							'Invitations are heading out to new team members...',
						);
						this.args.onAdminInvite();
					} else {
						const onNonAdminInvite = this.args.onNonAdminInvite;
						if (onNonAdminInvite) {
							onNonAdminInvite();
						}
						this.showInviteForm = false;
					}
				})
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService))
				.finally(() => {
					this.submitInProgress = false;
				});
		} else {
			this.submitInProgress = false;
			this.infolayerService.error(
				`There are available only ${this.availableNumberOfUsers} free user spaces!`,
			);
		}
	}

	@action
	close(): void {
		this.args.onClose();
	}
	<template>
		{{#if this.currentUserService.isAdmin}}
			<UserAdminInviteComponent
				@onInvite={{this.invite}}
				@onClose={{this.close}}
				@onContentChange={{@onContentChange}}
				@submitInProgress={{this.submitInProgress}}
				@maximumNumberOfNewUsers={{this.maximumNumberOfNewUsers}}
				@initialNumberOfNewUsers={{@initialNumberOfNewUsers}}
				@ignoreEmptyFields={{@ignoreEmptyFields}}
			/>
		{{else}}
			{{#if this.showInviteForm}}
				<UserNonAdminInviteComponent
					@submitInProgress={{this.submitInProgress}}
					@onInvite={{this.invite}}
					@onClose={{this.close}}
				/>
			{{else}}
				<div class="success">
					<CommonUiIconComponent @name="Clock" @size="sm" />
					<div class="success__message">
						New users invitations are heading out to account administrator(s)
						<strong>to be confirmed first.</strong>
					</div>
					<a
						role="button"
						class="button done theme-grey-outlined size-big"
						{{on "click" this.close}}
					>
						Done
					</a>
				</div>
			{{/if}}
		{{/if}}
	</template>
}
